import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { collection, getDocs, addDoc, deleteDoc, doc, Timestamp } from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import Sidebar from '../components/Sidebar/Sidebar';
import { useAdminRole } from '../hooks/useAdminRole';
import './BlockedSlotsPage.css';

const BlockedSlotsPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [blockedSlots, setBlockedSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const { role: userRole } = useAdminRole();

  // Formulaire pour ajouter un créneau bloqué
  const [formData, setFormData] = useState({
    dateFrom: '',
    dateTo: '',
    timeFrom: '',
    timeTo: '',
    reason: 'fully_booked', // 'fully_booked', 'temporarily_closed', 'resting_date'
    message: ''
  });

  // Vérifier l'état d'authentification
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      if (!currentUser) {
        setLoading(false);
        navigate('/admin');
      } else {
        await loadBlockedSlots();
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  // Charger les créneaux bloqués depuis Firestore
  const loadBlockedSlots = async () => {
    setLoadingSlots(true);
    try {
      const blockedSlotsRef = collection(db, 'blockedSlots');
      const querySnapshot = await getDocs(blockedSlotsRef);
      
      const slots = [];
      querySnapshot.forEach((docSnap) => {
        slots.push({
          id: docSnap.id,
          ...docSnap.data()
        });
      });
      
      // Trier par date puis par heure côté client
      slots.sort((a, b) => {
        const aStartDate = a.dateFrom || a.date || '';
        const bStartDate = b.dateFrom || b.date || '';
        if (aStartDate !== bStartDate) {
          return bStartDate.localeCompare(aStartDate); // Date décroissante
        }
        return (a.timeFrom || a.time || '').localeCompare(b.timeFrom || b.time || '');
      });
      
      setBlockedSlots(slots);
    } catch (error) {
      console.error('Erreur lors du chargement des créneaux bloqués:', error);
      setError('Erreur lors du chargement des créneaux bloqués');
    } finally {
      setLoadingSlots(false);
    }
  };

  // Générer les heures disponibles (12:00 à 21:30 par incréments de 30 min)
  const generateTimeSlots = () => {
    const slots = [];
    for (let hour = 12; hour <= 21; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        slots.push(timeString);
      }
    }
    return slots;
  };

  const timeSlots = generateTimeSlots();

  // Obtenir le message de raison
  const getReasonLabel = (reason) => {
    const reasons = {
      fully_booked: 'Complet',
      temporarily_closed: 'Fermé temporairement',
      resting_date: 'Jour de repos'
    };
    return reasons[reason] || reason;
  };

  // Obtenir le message pour le client
  const getClientMessage = (reason, customMessage) => {
    if (customMessage && customMessage.trim()) {
      return customMessage;
    }
    
    const defaultMessages = {
      fully_booked: 'Désolé, ce créneau est complet. Veuillez choisir un autre horaire.',
      temporarily_closed: 'Nous sommes fermés à ce moment. Veuillez choisir un autre horaire.',
      resting_date: 'Nous sommes fermés ce jour. Veuillez choisir une autre date.'
    };
    
    return defaultMessages[reason] || 'Ce créneau n\'est pas disponible. Veuillez choisir un autre horaire.';
  };

  // Gérer le changement de formulaire
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Vérifier si deux plages d'heures se chevauchent
  const timeRangesOverlap = (from1, to1, from2, to2) => {
    const timeToMinutes = (time) => {
      const [hours, minutes] = time.split(':').map(Number);
      return hours * 60 + minutes;
    };
    
    const from1Min = timeToMinutes(from1);
    const to1Min = timeToMinutes(to1);
    const from2Min = timeToMinutes(from2);
    const to2Min = timeToMinutes(to2);
    
    return (from1Min < to2Min && to1Min > from2Min);
  };

  const dateRangesOverlap = (from1, to1, from2, to2) => {
    return from1 <= to2 && to1 >= from2;
  };

  const getDatesInRange = (startDate, endDate) => {
    const dates = [];
    const current = new Date(startDate + 'T00:00:00');
    const end = new Date(endDate + 'T00:00:00');

    while (current <= end) {
      const y = current.getFullYear();
      const m = String(current.getMonth() + 1).padStart(2, '0');
      const d = String(current.getDate()).padStart(2, '0');
      dates.push(`${y}-${m}-${d}`);
      current.setDate(current.getDate() + 1);
    }

    return dates;
  };

  // Ajouter un créneau bloqué
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!formData.dateFrom || !formData.dateTo || !formData.timeFrom || !formData.timeTo) {
      setError('Veuillez sélectionner une plage de dates et une plage d\'heures');
      return;
    }

    if (formData.dateTo < formData.dateFrom) {
      setError('La date de fin doit être après ou égale à la date de début');
      return;
    }

    // Vérifier que l'heure de fin est après l'heure de début
    if (formData.timeTo <= formData.timeFrom) {
      setError('L\'heure de fin doit être après l\'heure de début');
      return;
    }

    try {
      const datesToCreate = getDatesInRange(formData.dateFrom, formData.dateTo);

      for (const date of datesToCreate) {
        const overlappingSlot = blockedSlots.find(slot => {
          const slotDateFrom = slot.dateFrom || slot.date;
          const slotDateTo = slot.dateTo || slot.date;
          if (!slotDateFrom || !slotDateTo) return false;

          const hasDateOverlap = dateRangesOverlap(date, date, slotDateFrom, slotDateTo);
          if (!hasDateOverlap) return false;

          const slotTimeFrom = slot.timeFrom || slot.time;
          const slotTimeTo = slot.timeTo || slot.time;
          if (!slotTimeFrom || !slotTimeTo) return false;

          return timeRangesOverlap(formData.timeFrom, formData.timeTo, slotTimeFrom, slotTimeTo);
        });

        if (overlappingSlot) {
          setError(`Le ${date}, cette plage chevauche un créneau déjà bloqué`);
          return;
        }
      }

      // Créer un blocage par jour sur toute la période sélectionnée
      await Promise.all(
        datesToCreate.map((date) =>
          addDoc(collection(db, 'blockedSlots'), {
            date,
            timeFrom: formData.timeFrom,
            timeTo: formData.timeTo,
            reason: formData.reason,
            message: formData.message.trim() || null,
            createdAt: Timestamp.now(),
            createdBy: user.uid
          })
        )
      );

      setSuccess(`${datesToCreate.length} jour(s) bloqué(s) avec succès`);
      setFormData({
        dateFrom: '',
        dateTo: '',
        timeFrom: '',
        timeTo: '',
        reason: 'fully_booked',
        message: ''
      });
      
      await loadBlockedSlots();
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Erreur lors de l\'ajout du créneau bloqué:', error);
      setError('Erreur lors de l\'ajout du créneau bloqué: ' + error.message);
    }
  };

  // Supprimer un créneau bloqué
  const handleDelete = async (slotId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir débloquer ce créneau ?')) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'blockedSlots', slotId));
      setSuccess('Créneau débloqué avec succès');
      await loadBlockedSlots();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      setError('Erreur lors de la suppression: ' + error.message);
    }
  };

  // Formater la date pour l'affichage
  const formatDate = (dateString) => {
    const date = new Date(dateString + 'T00:00:00');
    const days = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
    const months = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];
    return `${days[date.getDay()]} ${date.getDate()} ${months[date.getMonth()]}`;
  };

  if (loading) {
    return (
      <div className="blocked-slots-page">
        <div className="loading-container">
          <p>Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="blocked-slots-page">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} userRole={userRole} />
      
      <div className="blocked-slots-content">
        <header className="blocked-slots-header">
          <button 
            className="sidebar-toggle-btn"
            onClick={() => setSidebarOpen(true)}
            aria-label="Toggle sidebar"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="3" y1="12" x2="21" y2="12" />
              <line x1="3" y1="6" x2="21" y2="6" />
              <line x1="3" y1="18" x2="21" y2="18" />
            </svg>
          </button>
          <h1>Gérer les Créneaux Désactivés</h1>
        </header>

        <main className="blocked-slots-main">
          {error && (
            <div className="message error-message">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
              {error}
            </div>
          )}

          {success && (
            <div className="message success-message">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="20 6 9 17 4 12" />
              </svg>
              {success}
            </div>
          )}

          {/* Formulaire pour ajouter un créneau bloqué */}
          <div className="blocked-slots-form-card">
            <h2>Bloquer un Créneau</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <div className="form-group">
                  <label htmlFor="dateFrom">Date de début *</label>
                  <input
                    type="date"
                    id="dateFrom"
                    name="dateFrom"
                    value={formData.dateFrom}
                    onChange={handleChange}
                    required
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="dateTo">Date de fin *</label>
                  <input
                    type="date"
                    id="dateTo"
                    name="dateTo"
                    value={formData.dateTo}
                    onChange={handleChange}
                    required
                    min={formData.dateFrom || new Date().toISOString().split('T')[0]}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="timeFrom">Heure de début *</label>
                  <select
                    id="timeFrom"
                    name="timeFrom"
                    value={formData.timeFrom}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Sélectionner</option>
                    {timeSlots.map((time) => (
                      <option key={time} value={time}>
                        {time}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="timeTo">Heure de fin *</label>
                  <select
                    id="timeTo"
                    name="timeTo"
                    value={formData.timeTo}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Sélectionner</option>
                    {timeSlots
                      .filter(time => !formData.timeFrom || time > formData.timeFrom)
                      .map((time) => (
                        <option key={time} value={time}>
                          {time}
                        </option>
                      ))}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="reason">Raison *</label>
                <select
                  id="reason"
                  name="reason"
                  value={formData.reason}
                  onChange={handleChange}
                  required
                >
                  <option value="fully_booked">Complet</option>
                  <option value="temporarily_closed">Fermé temporairement</option>
                  <option value="resting_date">Jour de repos</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="message">Message personnalisé (optionnel)</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Message à afficher au client (si vide, un message par défaut sera utilisé)"
                  rows="3"
                />
                <small className="form-hint">
                  Si laissé vide, un message par défaut sera affiché selon la raison sélectionnée.
                </small>
              </div>

              <button type="submit" className="btn-primary">
                Bloquer le Créneau
              </button>
            </form>
          </div>

          {/* Liste des créneaux bloqués */}
          <div className="blocked-slots-list-card">
            <h2>Créneaux Bloqués ({blockedSlots.length})</h2>
            
            {loadingSlots ? (
              <div className="loading-container">
                <p>Chargement...</p>
              </div>
            ) : blockedSlots.length === 0 ? (
              <div className="empty-state">
                <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                  <line x1="16" y1="2" x2="16" y2="6" />
                  <line x1="8" y1="2" x2="8" y2="6" />
                  <line x1="3" y1="10" x2="21" y2="10" />
                </svg>
                <p>Aucun créneau bloqué</p>
              </div>
            ) : (
              <div className="blocked-slots-table">
                <table>
                  <thead>
                    <tr>
                      <th>Période</th>
                      <th>Heure</th>
                      <th>Raison</th>
                      <th>Message Client</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {blockedSlots.map((slot) => (
                      <tr key={slot.id}>
                        <td>
                          {(slot.dateFrom || slot.dateTo) && (slot.dateFrom !== slot.dateTo) ? (
                            `${formatDate(slot.dateFrom || slot.date)} → ${formatDate(slot.dateTo || slot.date)}`
                          ) : (
                            formatDate(slot.date || slot.dateFrom)
                          )}
                        </td>
                        <td>{(slot.timeFrom || slot.time)} - {(slot.timeTo || slot.time)}</td>
                        <td>
                          <span className={`reason-badge reason-${slot.reason}`}>
                            {getReasonLabel(slot.reason)}
                          </span>
                        </td>
                        <td className="message-cell">
                          {slot.message ? (
                            <span title={slot.message}>{slot.message.substring(0, 50)}{slot.message.length > 50 ? '...' : ''}</span>
                          ) : (
                            <span className="default-message">{getClientMessage(slot.reason)}</span>
                          )}
                        </td>
                        <td>
                          <button
                            className="btn-delete"
                            onClick={() => handleDelete(slot.id)}
                            title="Débloquer ce créneau"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <polyline points="3 6 5 6 21 6" />
                              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                            </svg>
                            Débloquer
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default BlockedSlotsPage;

